import { useEffect, useState } from "react";
import { GetFunction } from "src/utils/axios_connection";

// The field library (GET /user/v1/fields): the named fields a passport scan
// or saved profile can fill. Linking a question to one is what makes it
// fill itself for the traveller, and puts it in the agent's copy view.
let cachedFields = null;
let pending = null;

function loadFields() {
  if (!pending) {
    pending = GetFunction({ Route: "v1/fields", is_client: true, showalert: false })
      .then((response) => {
        const groups = Object.fromEntries(
          (response?.data?.groups || []).map((group) => [group.key, group.label])
        );
        cachedFields = (response?.data?.fields || []).map((field) => ({
          key: field.key,
          // "Passport · Passport number (from passport)"
          label: `${groups[field.group] || field.group} · ${field.label}${
            field.source === "passport" ? " (from passport)" : ""
          }`,
          name: field.label,
          // text, date, select, country, phone, email, number, boolean: what
          // kind of answer the field holds, so the editor can say which
          // question type fits it.
          type: field.type,
          options: (field.options || []).map((option) => option.label),
          // passport, profile, document or ask: where the answer comes from.
          source: field.source,
        }));
        return cachedFields;
      })
      .catch(() => {
        pending = null; // let the next screen try again
        return null;
      });
  }
  return pending;
}

/**
 * { fields, failed }. The list arrives after the first render; anything that
 * builds on it must read it at render time, not capture it once.
 */
export function useFieldLibrary() {
  const [fields, setFields] = useState(cachedFields || []);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (cachedFields) return undefined;
    let live = true;
    loadFields().then((loaded) => {
      if (!live) return;
      if (loaded) setFields(loaded);
      else setFailed(true);
    });
    return () => {
      live = false;
    };
  }, []);
  return { fields, failed };
}

/**
 * The question type that suits a library field. "country" fields have no
 * country picker on the form: Nationality lists nationalities ("Lebanese"),
 * which is right only for the nationality fields themselves.
 */
export function questionTypeForField(field) {
  if (!field) return null;
  switch (field.type) {
    case "date":
      return "date";
    case "select":
    case "boolean":
      return "list";
    case "number":
      return "number";
    case "country":
      return field.key.startsWith("nationality") ? "nationality" : "text";
    case "phone":
      return "phone";
    case "email":
      return "email";
    default:
      return "text";
  }
}
